import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  CalendarDays, Plus, Clock, CheckCircle2, XCircle, AlertCircle,
  Loader2, PlusCircle, X,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { apiClient } from "../services/api";
import { toast } from "sonner";

const daysBetween = (a, b) => {
  if (!a || !b) return 0;
  const diff = (new Date(b) - new Date(a)) / 86400000;
  return diff >= 0 ? Math.round(diff) + 1 : 0;
};

const NewAbsenceModal = ({ onClose, onCreated }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    absence_type: "paid_leave",
    start_date: "",
    end_date: "",
    reason: "",
    half_day_start: false,
    half_day_end: false,
  });
  const [saving, setSaving] = useState(false);

  const days = daysBetween(form.start_date, form.end_date);

  const TYPE_OPTIONS = [
    "paid_leave", "unpaid_leave", "sick_leave", "training",
    "business_trip", "maternity", "paternity", "bereavement", "hajj", "other",
  ];

  const submit = async (e) => {
    e.preventDefault();
    if (!form.start_date || !form.end_date) {
      toast.error(t("portal.leaves.toasts.datesRequired"));
      return;
    }
    if (new Date(form.end_date) < new Date(form.start_date)) {
      toast.error(t("portal.leaves.toasts.endBeforeStart"));
      return;
    }
    setSaving(true);
    try {
      await apiClient.post("v1/absences", form);
      toast.success(t("portal.leaves.toasts.submitSuccess"));
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail ?? t("common.operationError"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="rounded-xl w-full max-w-md" style={{ backgroundColor: "var(--lm-s1)", border: "1px solid var(--lm-border)" }}>
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="font-semibold">{t("portal.leaves.newLeaveTitle")}</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={submit} className="p-4 space-y-4">
          <div>
            <Label htmlFor="absence_type">{t("portal.leaves.type")}</Label>
            <select
              id="absence_type"
              className="w-full mt-1 border border-border rounded-md px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              value={form.absence_type}
              onChange={(e) => setForm((f) => ({ ...f, absence_type: e.target.value }))}
            >
              {TYPE_OPTIONS.map((val) => (
                <option key={val} value={val}>{t(`portal.leaves.types.${val}`)}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="start_date">{t("portal.leaves.startDate")}</Label>
              <Input
                id="start_date"
                type="date"
                className="mt-1"
                value={form.start_date}
                onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))}
                required
              />
            </div>
            <div>
              <Label htmlFor="end_date">{t("portal.leaves.endDate")}</Label>
              <Input
                id="end_date"
                type="date"
                className="mt-1"
                value={form.end_date}
                min={form.start_date}
                onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))}
                required
              />
            </div>
          </div>

          {days > 0 && (
            <p className="text-xs text-muted-foreground">
              {t("portal.leaves.duration")} : <strong>{days} jour{days > 1 ? "s" : ""}</strong>
            </p>
          )}

          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                className="rounded"
                checked={form.half_day_start}
                onChange={(e) => setForm((f) => ({ ...f, half_day_start: e.target.checked }))}
              />
              {t("portal.leaves.halfDayStart")}
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                className="rounded"
                checked={form.half_day_end}
                onChange={(e) => setForm((f) => ({ ...f, half_day_end: e.target.checked }))}
              />
              {t("portal.leaves.halfDayEnd")}
            </label>
          </div>

          <div>
            <Label htmlFor="reason">{t("portal.leaves.reason")}</Label>
            <textarea
              id="reason"
              className="w-full mt-1 border border-border rounded-md px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              rows={3}
              placeholder={t("portal.leaves.reasonPlaceholder")}
              value={form.reason}
              onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
            />
          </div>

          <div className="flex gap-2 justify-end pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <PlusCircle className="w-4 h-4 mr-2" />}
              {t("portal.leaves.submit", t("common.submit"))}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const { t } = useTranslation();
  const icons = {
    pending: Clock,
    approved: CheckCircle2,
    rejected: XCircle,
    cancelled: AlertCircle,
  };
  const colors = {
    pending:   "bg-yellow-950/30 text-yellow-400",
    approved:  "bg-green-950/30 text-green-400",
    rejected:  "bg-red-950/30 text-red-400",
    cancelled: "bg-muted text-muted-foreground",
  };
  const Icon = icons[status] ?? Clock;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${colors[status] ?? colors.pending}`}>
      <Icon className="w-3 h-3" />
      {t(`portal.leaves.status.${status}`, status)}
    </span>
  );
};

const AbsenceRow = ({ absence, onCancel, cancelling }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || "fr";
  const start = new Date(absence.start_date).toLocaleDateString(locale);
  const end = new Date(absence.end_date).toLocaleDateString(locale);
  const canCancel = onCancel && absence.status === "pending";

  return (
    <div className="flex items-center justify-between p-3 border rounded-lg">
      <div className="flex items-start gap-3">
        <CalendarDays className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
        <div>
          <p className="font-medium text-sm">{t(`portal.leaves.types.${absence.absence_type}`, absence.absence_type)}</p>
          <p className="text-xs text-muted-foreground">
            {start} → {end} · {absence.days_count} jour{absence.days_count > 1 ? "s" : ""}
          </p>
          {absence.reason && (
            <p className="text-xs text-muted-foreground mt-0.5 italic">{absence.reason}</p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <StatusBadge status={absence.status} />
        {canCancel && (
          <Button
            size="sm"
            variant="ghost"
            className="text-red-400 hover:text-red-300 hover:bg-red-950/20 h-7 px-2"
            disabled={cancelling === absence.id}
            onClick={() => onCancel(absence.id)}
          >
            {cancelling === absence.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" />}
          </Button>
        )}
      </div>
    </div>
  );
};

const MesConges = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [cancelling, setCancelling] = useState(null);

  const absencesQ = useQuery({
    queryKey: ['my-absences'],
    queryFn: () => apiClient.get("v1/absences/me?limit=50").then(r => r.data.items ?? r.data ?? []),
  });

  const absences = absencesQ.data ?? [];
  const loading = absencesQ.isPending;
  const noProfile = absencesQ.isError && absencesQ.error?.response?.status === 404;

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['my-absences'] });

  const handleCancel = async (id) => {
    setCancelling(id);
    try {
      await apiClient.post(`v1/absences/${id}/cancel`);
      toast.success(t("portal.leaves.toasts.cancelSuccess"));
      invalidate();
    } catch (err) {
      toast.error(err.response?.data?.detail ?? t("portal.leaves.toasts.cancelError"));
    } finally {
      setCancelling(null);
    }
  };

  const pending = absences.filter((a) => a.status === "pending");
  const history = absences.filter((a) => a.status !== "pending");

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (noProfile) {
    return (
      <div className="flex flex-col items-center gap-3 py-16 text-center">
        <AlertCircle className="w-10 h-10 text-muted-foreground" />
        <p className="font-medium">{t("portal.leaves.noEmployeeProfile", "No employee profile found")}</p>
        <p className="text-sm text-muted-foreground">{t("portal.leaves.contactHR", "Contact HR to link your account to an employee record.")}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div />
        <Button onClick={() => setShowModal(true)}>
          <Plus className="w-4 h-4 mr-2" />
          {t("portal.leaves.newRequest")}
        </Button>
      </div>

      {pending.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="w-4 h-4 text-yellow-600" />
              {t("portal.leaves.pendingApproval")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {pending.map((a) => (
              <AbsenceRow key={a.id} absence={a} onCancel={handleCancel} cancelling={cancelling} />
            ))}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("portal.leaves.history")}</CardTitle>
        </CardHeader>
        <CardContent>
          {absences.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-10 text-center">
              <CalendarDays className="w-10 h-10 text-muted-foreground" />
              <p className="font-medium">{t("portal.leaves.noRequests")}</p>
              <p className="text-sm text-muted-foreground">{t("portal.leaves.noRequestsDesc")}</p>
            </div>
          ) : history.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              {t("portal.leaves.noPending")}
            </p>
          ) : (
            <div className="space-y-2">
              {history.map((a) => (
                <AbsenceRow key={a.id} absence={a} onCancel={null} cancelling={null} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {showModal && (
        <NewAbsenceModal
          onClose={() => setShowModal(false)}
          onCreated={() => { setShowModal(false); invalidate(); }}
        />
      )}
    </div>
  );
};

export default MesConges;
